import React, { FC, useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { map, keyBy } from '@dt/fe-utils/dist/lodash'
import { IonModal, IonContent, IonHeader, IonSlides, IonSlide } from '@ionic/react'
import { toast, getCouponsInfo, formatCardNumber, getPercent, getMemberCardStatus } from '@utils'
import { getLevelUpGrowth } from 'common/utils/member'
import { format, toPrice } from '@dt/fe-utils'
import { storeMobx } from 'restaurant'
import ActivityApi from '@api/activity.api'
import MemberCardApi from '@api/memberCard.api'
import { memberMobx } from '@mobxStore'
import { CardIntegralEquity } from '@cps'
import QRCode from 'qrcode.react'
import LevelTable from '../LevelTable'
import CateTab from '../CateTab'
import './index.scss'

interface IProps {
  cardInfo: any;
  reject?: () => void;
  resolve?: (path: string) => void;
}

const CardDetail: FC<IProps> = (props) => {
  const deductionRules = {
    rechargeFirst: i18n.t('mkt.member.deductionRule2'),
    average: i18n.t('mkt.member.deductionRule1'),
    giveFirst: i18n.t('mkt.member.deductionRule3')
  }
  const initCates = [
    {
      label: i18n.t('mkt.member.cardOperation'),
      value: 0
    },
    {
      label: i18n.t('mkt.member.memberDetails'),
      value: 1
    }
  ]
  const { cardInfo, reject, resolve } = props
  const statusInfo = getMemberCardStatus(cardInfo, 3)
  const { storeList, storeIdToStore } = storeMobx
  const [couponDetails, setCouponDetails] = useState<any>({})
  const [levelInfo, setLevelInfo] = useState<any[]>([])
  const [isLike, setIsLike] = useState<boolean>(false)
  const [cateList, setCateList] = useState<any>(initCates)
  const [curSlide, setCurSlide] = useState(0)
  const [isLoad, setIsLoad] = useState<boolean>(false)
  const initOpts = {
    initialSlide: curSlide,
    autoplay: false,
    loop: false,
  }
  const couponPolicys = useMemo(() => {
    if (cardInfo && cardInfo.card.isSendCoupon && cardInfo.card.couponPolicys.length > 0) {
      return keyBy(cardInfo.card.couponPolicys, (item: any) => {
        if (item.sendType === 'action') {
          return `${item.sendType}_${item.sendValue}`
        } else {
          return item.sendType
        }
      })
    } else {
      return {}
    }
  }, [cardInfo])
  const handleStoreName = (isAllStore: boolean, storeIds: any[]) => {
    if (isAllStore) {
      return storeList.map((i: any) => {
        return i.storeName
      })
    } else {
      const list: any = []
      map(storeIds, (i: any) => {
        const findItem = storeIdToStore[i]
        if (findItem) list.push(findItem.storeName)
      })
      return list
    }
  }
  const getCoupDetail = (coupons: any[]) => {
    const list: any[] = []
    map(coupons, (i: any) => {
      if (couponDetails[i.couponId]) list.push(`${couponDetails[i.couponId].couponName}${i.couponCnt > 1 ? ` (x${i.couponCnt})` : ''}`)
    })
    return list
  }
  const handleGo = (path: string) => {
    if (cardInfo.status !== 'enable') {
      toast({ message: i18n.t('mkt.giftCard.frozen') })
      return
    }
    resolve(path)
  }
  const goRecord = (type: string) => {
    if (['consume', 'integral'].includes(type)) {
      resolve(`/iRecord/flow/${type}/${cardInfo.cardNo}`)
    } else if (['isDefault', 'noDefault'].includes(type)) {
      const isDefault: boolean = type !== 'isDefault'
      memberMobx.setDefaultCard({ memberId: cardInfo.memberId, memberCardId: cardInfo.memberCardId, isDefault }).then((res) => {
        if (!res.error) setIsLike(isDefault)
      })
    }
  }
  // Component Effect
  useEffect(() => {
    MemberCardApi.getLevelCardList(cardInfo.cardId).then((res: any) => {
      if (!res.error) {
        if (res.length > 1 || res[0].isLevel) {
          const newList = initCates
          newList.push({ label: i18n.t('mkt.member.levelDetails'), value: 2 })
          setCateList(newList)
          setLevelInfo(res)
        } else {
          setLevelInfo([])
        }
      }
      if (cardInfo.detailType) setCurSlide(cardInfo.detailType)
      setIsLoad(true)
    })
  }, [])
  useEffect(() => {
    if (cardInfo && cardInfo.card.isSendCoupon && cardInfo.card.couponPolicys.length > 0) {
      let coupons: any = []
      cardInfo.card.couponPolicys.map((item: any) => {
        coupons = coupons.concat(item.coupons)
      })
      if (coupons.length > 0) {
        ActivityApi.getCouponsList({ couponIds: getCouponsInfo(coupons) }).then((res: any) => {
          if (res?.length > 0) {
            setCouponDetails(keyBy(res, 'couponId'))
          }
        })
      }
    }
    if (cardInfo) setIsLike(!!cardInfo.isDefault)
  }, [cardInfo])
  return (
    <IonModal
      cssClass='memberCard-detail-modal'
      isOpen
      showBackdrop
      swipeToClose
      backdropDismiss
      onDidDismiss={reject}
    >
      <IonHeader className='modal-header'>
        <span>{cardInfo.card.cardName}</span>
        <i data-cypid='mmgHeaderBack' className='iconfont icon-close' onClick={reject}></i>
      </IonHeader>
      <div className='basic-info-block'>
        {
          (cardInfo.card.allowAgainLoad || cardInfo.card.allowCustomizeLoad || cardInfo.card.discountType || cardInfo.card.mallDiscountType || statusInfo.status > 1 || cardInfo.card.isIntegral || cardInfo.card.isLevel) &&
            <div className='card-basic-info'>
              {
                (cardInfo.card.allowAgainLoad || cardInfo.card.allowCustomizeLoad || cardInfo.balance > 0) && <span>{i18n.t('mkt.common.balance')}: {toPrice(cardInfo.balance || 0)}</span>
              }
              {
                statusInfo.status === 1 ? (
                  <>
                    {
                      cardInfo.card.discountType && <span>{cardInfo.card?.products?.length ? i18n.t('mkt.member.someMeals') : i18n.t('order.meals')}: {cardInfo.card.discountType === 'memberPrice' ? i18n.t('checkout.memberPrice') : format.toDiscount('discountRate', cardInfo.card.discountValue)}</span>
                    }
                    {cardInfo.card.mallDiscountType === 'memberPrice' && <span>{i18n.t('order.mall')}: {i18n.t('checkout.memberPrice')}</span>}
                    {
                      cardInfo.card.mallDiscountType === 'discountRate' && !!cardInfo.card.mallDiscountValue && <span>{cardInfo.card?.mallProducts?.length ? i18n.t('mkt.member.someMall') : i18n.t('order.mall')}: {format.toDiscount('discountRate', cardInfo.card.mallDiscountValue)}</span>
                    }
                  </>
                ) : null
              }
              {
                cardInfo.card.isIntegral && <span>{i18n.t('mkt.common.integral')}: {cardInfo.integral || 0}</span>
              }
              {
                cardInfo.card.isLevel && <span>{i18n.t('mkt.member.growthValue')}: {cardInfo.growth}{cardInfo.card.nextLevelInfo && `/${cardInfo.card.nextLevelInfo.levelGrowth}`}</span>
              }
            </div>
        }
        {
          statusInfo.status > 1 &&
            <div className='card-basic-info'>
              <span className='expire-text'>
                {statusInfo.status === 4 && (
                  <>
                    {statusInfo.expireDay === 1 ? statusInfo.isToday ? i18n.t('mkt.member.todayExpires', { time: format.toTime(statusInfo.expireTime) }) : i18n.t('mkt.member.tomorrowExpires', { time: format.toTime(statusInfo.expireTime) }) : i18n.t('mkt.member.expiresInSomeDays', { day: statusInfo.expireDay - 1 })}
                  </>
                )}
                {statusInfo.status === 3 && i18n.t('mkt.member.expiredTip')}
                {statusInfo.status === 2 && i18n.t('mkt.giftCard.frozen')}
              </span>
            </div>
        }
        <CateTab
          list={cateList}
          selected={cateList[curSlide].value}
          onChange={(val: any) => {
            if (curSlide !== val) {
              (document.getElementById('couponSlider') as any).slideTo(val, 0)
            }
            setCurSlide(val)
          }}
        >
        </CateTab>
      </div>
      <IonContent>
        {
          cardInfo &&
            <div className='modal-body'>
              <div className='mkt-detail-area'>
                {
                  isLoad &&
                    <IonSlides id='couponSlider' options={initOpts} onIonSlideDidChange={(e: any) => { setCurSlide(e.target.swiper.activeIndex) }}>
                      <IonSlide>
                        <div className='slide-box'>
                          <div className='code-area'>
                            <QRCode value={cardInfo.cardNo} size={280} level='H' />
                            <div className='cardno'>{isLike && <i className='iconfont icon-like'></i>}{formatCardNumber(cardInfo.cardNo)}</div>
                          </div>
                          <div className='footer'>
                            <div className='footer-block'>
                              {
                                cardInfo.card.allowAgainLoad || cardInfo.card.allowCustomizeLoad
                                  ? (
                                    <div
                                      data-cypid='mmgRechargeMCard'
                                      className='btn recharge-btn'
                                      onClick={() => { handleGo(`/iCard/cardRecharge/${cardInfo.memberCardId}`) }}
                                    >
                                      <i className={`iconfont opt-icon ${window.bo === 'cn' ? 'icon-yueb' : 'icon-meiyuan'}`}></i>
                                      {i18n.t('mkt.member.rechargeBalance')}
                                    </div>
                                  ) : ''
                              }
                              {
                                cardInfo.card.type === 'paid' && cardInfo.expireTime &&
                                  <div
                                    data-cypid='mmgBuyMCard'
                                    className='btn expireRenew-btn'
                                    onClick={() => { handleGo(`/iCard/cardOpen/buy/${cardInfo.memberCardId}`) }}
                                  >
                                    <i className='iconfont icon-zuanshi opt-icon'></i>
                                    {i18n.t('mkt.member.expireRenew')}
                                  </div>
                              }
                              {
                                isLike
                                  ? <div className='btn' data-cypid='mmgChangeDefaultMCard' onClick={() => goRecord('isDefault')}><i className='iconfont icon-like opt-icon'></i>{i18n.t('mkt.member.memberCancle')}</div>
                                  : <div className='btn' data-cypid='mmgChangeDefaultMCard' onClick={() => goRecord('noDefault')}><i className='iconfont icon-like1 opt-icon'></i>{i18n.t('mkt.member.memberDefault')}</div>
                              }
                              {
                                cardInfo.card.allowAgainLoad || cardInfo.card.allowCustomizeLoad
                                  ? <div data-cypid='mmgToMCardConsume' className='btn' onClick={() => goRecord('consume')}><i className='iconfont icon-chongzhiliushui opt-icon'></i>{i18n.t('mkt.member.rechargeFlow')}</div> : ''
                              }
                              {
                                cardInfo.card.isIntegral &&
                                  <div data-cypid='mmgToMCardIntegral' className='btn' onClick={() => goRecord('integral')}><i className='iconfont icon-jifen1 opt-icon'></i>{i18n.t('mkt.member.integralFlow')}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </IonSlide>
                      <IonSlide>
                        <div className='slide-box'>
                          {
                        cardInfo.card?.extra.cardRightsDes &&
                          <div className='card-info-block'>
                            <div className='title'>{i18n.t('mkt.member.memberCardDes')}</div>
                            <div className='des'><span>{cardInfo.card.extra.cardRightsDes}</span></div>
                          </div>
                          }
                          {
                            (cardInfo.card.allowAgainLoad || cardInfo.card.allowCustomizeLoad || cardInfo.balance > 0) &&
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('mkt.member.memberCardRecharge')}</div>
                                <div className='des'>
                                  <div>{i18n.t('mkt.member.lumpSum')}: {toPrice(cardInfo.balance || 0)} {!!cardInfo.balanceGive && `(${i18n.t('mkt.member.balanceGive', { give: toPrice(cardInfo.balanceGive) })})`}</div>
                                  {window.bo === 'cn' && <div>{i18n.t('mkt.member.mallConsume')}: {cardInfo.card.supportMallPay ? i18n.t('mkt.common.available') : i18n.t('mkt.common.unavailable')}</div>}
                                  {window.bo !== 'cn' && <div>{i18n.t('mkt.member.tipPayment')}: {cardInfo.card.isPayTip ? i18n.t('mkt.common.available') : i18n.t('mkt.common.unavailable')}</div>}
                                  <div>{i18n.t('mkt.member.deductionRules')}: {deductionRules[cardInfo.card.deductionRule]}</div>
                                </div>
                              </div>
                          }
                          {
                            (cardInfo.card.discountType || cardInfo.card.mallDiscountType) &&
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('mkt.member.memberDiscountBenefits')}</div>
                                {cardInfo.card.discountType && <div className='des'>{cardInfo.card?.products?.length ? i18n.t('mkt.member.someMealConsumeGet') : i18n.t('mkt.member.mealConsumeGet')}: {cardInfo.card.discountType === 'memberPrice' ? i18n.t('checkout.memberPrice') : format.toDiscount('discountRate', cardInfo.card.discountValue)}</div>}
                                {cardInfo.card.mallDiscountType && <div className='des'>{cardInfo.card?.mallProducts?.length ? i18n.t('mkt.member.someMallConsumeGet') : i18n.t('mkt.member.mallConsumeGet')}: {cardInfo.card.mallDiscountType === 'memberPrice' ? i18n.t('checkout.memberPrice') : format.toDiscount('discountRate', cardInfo.card.mallDiscountValue)}</div>}
                              </div>
                          }
                          {
                            cardInfo.card.isIntegral &&
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('cart.integralRule')}</div>
                                <div className='des'>
                                  <div>{i18n.t('mkt.member.currentPoints')}: {cardInfo.integral || 0}</div>
                                  <CardIntegralEquity cardInfo={cardInfo.card} />
                                </div>
                              </div>
                          }
                          <div className='card-info-block'>
                            <div className='title'>{i18n.t('mkt.member.applyStore')}</div>
                            <div className='des'>
                              {handleStoreName(cardInfo.card.isAllStore, cardInfo.card.storeIds).join(',')}
                            </div>
                          </div>
                          {
                            cardInfo.expireTime &&
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('mkt.member.cardExpireTime')}</div>
                                <div className='des'>{format.toDateTime(cardInfo.expireTime)}</div>
                              </div>
                          }
                          {
                            couponPolicys.time &&
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('mkt.member.couponSend')}</div>
                                <div className='des'>
                                  <div>{i18n.t(`mkt.member.sendTime${couponPolicys.time.sendValue}`)}</div>
                                  <div>{getCoupDetail(couponPolicys.time.coupons).join(',')}</div>
                                </div>
                              </div>
                          }
                          {
                            (couponPolicys.action_renewAndReceive || couponPolicys.action_renew) && cardInfo.card.type === 'paid' &&
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('mkt.member.openIssue')}</div>
                                <div className='des'>
                                  <div>{i18n.t('mkt.member.renewSend')}</div>
                                  <div>{getCoupDetail((couponPolicys.action_renewAndReceive || couponPolicys.action_renew).coupons).join(',')}</div>
                                </div>
                              </div>
                          }
                        </div>
                      </IonSlide>
                      {
                        levelInfo.length > 0 &&
                          <IonSlide>
                            <div className='slide-box'>
                              {
                                (cardInfo.card?.nextLevelInfo || cardInfo.card?.level > 1) &&
                                  <div className='levelinfo-value'>
                                    <div>
                                      <span>{cardInfo.card?.nextLevelInfo ? i18n.t('mkt.member.upgradePoi', { growth: getLevelUpGrowth(cardInfo.card?.nextLevelInfo?.levelGrowth, cardInfo.growth) }) : i18n.t('mkt.member.highestGrade')}</span>
                                      <span className='level-num'>LV{cardInfo.card?.nextLevelInfo ? cardInfo.card.level + 1 : cardInfo.card.level}</span>
                                    </div>
                                    <div className='pro-bar'>
                                      <div className='pro-bar-bg'></div>
                                      <div style={{ width: cardInfo.card.nextLevelInfo ? `${getPercent(cardInfo.growth, cardInfo.card.nextLevelInfo.levelGrowth) || 0}%` : '100%' }} className='pro-bar-completed'></div>
                                    </div>
                                    <div className='cur-growth-value'>{i18n.t('mkt.member.curGrowthValue')} {cardInfo.growth}</div>
                                  </div>
                              }
                              <div className='card-info-block'>
                                <div className='title'>{i18n.t('mkt.member.levelCardTit')}</div>
                                <div className='des'>
                                  <LevelTable levelInfo={levelInfo}></LevelTable>
                                </div>
                              </div>
                            </div>
                          </IonSlide>
                      }
                    </IonSlides>
                }
              </div>
            </div>
        }
      </IonContent>
    </IonModal>
  )
}

export default observer(CardDetail)
